import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, model, output } from '@angular/core';
import { type CalendarType } from '@modules/calendar-app/types/calendar-type';
import {
  beginningOfPeriodInZone,
  calculateDateInZone,
  endOfPeriodInZone,
} from '@modules/calendar-app/utils/display-zone-period.util';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { toFakeLocal } from '@modules/form-controls/types/timezone-convert';
import { isValidTimeZone } from '@sharedlib/common/utils/timezone';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';

@Component({
  standalone: true,
  selector: 'app-calendar-pagination',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './calendar-pagination.component.html',
  imports: [IconComponent, Button2Component],
})
export class CalendarPaginationComponent {
  private readonly LOCALE_ID = inject(LOCALE_ID);
  private readonly dateFormatService = inject(DateFormatService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  public readonly calendarType = input.required<CalendarType>();
  public readonly date = model.required<Date>();
  /** Disables Previous and Next, so a click cannot step past a period whose events have not loaded yet. */
  public readonly loading = input<boolean>(false);
  public readonly refresh = output();

  public readonly dateRangeText = computed(() => {
    const currentDate = this.date();
    const type = this.calendarType();

    if (!currentDate || !type) {
      return '';
    }

    // SBUX-829 N1: compute the period in the DISPLAY (account) zone so the label matches the grid.
    // The date-only formatters below read a Date's browser-local parts, so hand them a Date whose
    // browser-local wall clock equals the account-zone boundary (via toFakeLocal), not the raw UTC
    // instant — otherwise the rendered day shifts by the browser↔account offset.
    const timeZone = this.displayTimeZoneService.timeZone();
    const toDisplay = (instant: Date): Date => (isValidTimeZone(timeZone) ? toFakeLocal(instant, timeZone) : instant);
    const startDate = toDisplay(beginningOfPeriodInZone(currentDate, type, this.LOCALE_ID, timeZone));
    const endDate = toDisplay(endOfPeriodInZone(currentDate, type, this.LOCALE_ID, timeZone));

    // Format based on calendar type
    switch (type) {
      case 'year':
        return this.dateFormatService.formatYear(startDate);
      case 'month':
        return this.dateFormatService.formatMonthYear(startDate);
      case 'week':
      case 'workWeek':
        return this.dateFormatService.formatDateRange(startDate, endDate);
      case 'day':
      default:
        return this.dateFormatService.formatDate(startDate);
    }
  });

  public prevDate(): void {
    this.step(-1);
  }

  public nextDate(): void {
    this.step(1);
  }

  // SBUX-829 N1: step in the DISPLAY (account) zone so the selected date lands on the account-zone
  // period boundary — matching the grid — rather than the browser boundary.
  private step(direction: 1 | -1): void {
    const currentDate = this.date();
    const type = this.calendarType();
    if (!this.loading() && currentDate && type) {
      const timeZone = this.displayTimeZoneService.timeZone();
      const stepped = calculateDateInZone(currentDate, direction, type, timeZone);
      this.date.set(beginningOfPeriodInZone(stepped, type, this.LOCALE_ID, timeZone));
    }
  }
}
